import type { UseInfiniteQueryResult } from '@tanstack/react-query'
import { useState } from 'react'
import {
  type OrdersAutocompleteResponse,
  useOrdersAutocomplete
} from '~/api/queries/order'
import { Dropdown } from '~/components/Form'
import type {
  DropdownOption,
  SingleDropdownProps
} from '~/components/Form/Input/Dropdown'
import {
  optionItemElementLabel,
  optionItemElementLabelMain,
  optionItemElementSublabel
} from '~/components/Form/Input/Dropdown.css'
import { formatAddress } from '~/domains/common/utils'
import { formatDateTimeShort } from '~/utils/date'
import { formatOrderTitle } from '../utils'

const orderElement = (order: OrdersAutocompleteResponse) => {
  const formattedLocation = formatAddress(order.matchingLocation)
  return (
    <div className={optionItemElementLabel}>
      <div className={optionItemElementLabelMain}>
        {order.company.name} - {formatOrderTitle(order)}
      </div>
      <div className={optionItemElementSublabel}>
        Créée le {formatDateTimeShort(new Date(order.created))}
        {formattedLocation.length > 0 && <span> · {formattedLocation}</span>}
      </div>
    </div>
  )
}

export const OrdersAutocomplete = ({
  userId,
  companyId,
  ids,
  ...props
}: {
  userId?: number
  companyId?: number
  ids?: number[]
} & Omit<SingleDropdownProps, 'optionsInfiniteQuery' | 'options'>) => {
  const [search, setSearch] = useState<string>()
  const { data, ...rest } = useOrdersAutocomplete({
    ids: search !== undefined ? undefined : ids,
    search: search,
    onlyOpen: true,
    userId,
    companyId
  })
  const options = data?.pages
    .flatMap((page) => page['hydra:member'])
    .map((order) => {
      return {
        label: formatOrderTitle(order),
        value: String(order.id),
        element: orderElement(order)
      }
    })

  return (
    <Dropdown
      optionsInfiniteQuery={
        {
          ...rest,
          data: options
        } as unknown as UseInfiniteQueryResult<DropdownOption[] | undefined>
      }
      searchShouldFilterOptions={false}
      defaultValue={ids?.[0] ? String(ids?.[0]) : null}
      onSearchChange={setSearch}
      emptyMessage="Aucune commande trouvée"
      {...props}
    />
  )
}
